import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Avatar,
    Chip,
    Button,
    Stack,
    IconButton,
    Divider,
    Tabs,
    Tab,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Skeleton,
    alpha,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import {
    BatteryChargingFull as BatteryIcon,
    LocationOn as MapPinIcon,
    AccessTime as ClockIcon,
    CalendarMonth as CalendarIcon,
    Description as FileTextIcon,
    Download as DownloadIcon,
    Person as UserIcon,
    Phone as PhoneIcon,
    Assignment as FileSignatureIcon,
    ArrowBack as ArrowBackIcon,
    Bolt as BoltIcon,
    History as HistoryIcon,
    GpsFixed as GpsIcon,
    Construction as MachineIcon,
    Build as WrenchIcon,
    ErrorOutline as AlertCircleIcon,
    Edit as EditIcon,
    SwapHoriz as StatusIcon,
    Sms as SmsIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import mewpService from '../../services/mewpService';
import { formatToIST } from '../../utils/dataUtils';
import { resolveDeviceStatus } from '../../utils/liveStatus';
import LiveStatusBadge from '../ui/LiveStatusBadge';
import ChangeStatusDialog from './ChangeStatusDialog';
import { useAuth } from '../../context/AuthContext';
import { isSuperAdmin } from '../../utils/permissions';
import { statusLabel, statusColor } from '../../utils/statusLabels';

// Fix Leaflet marker
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { machineLabel } from '../../utils/machineLabel';
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});
L.Marker.prototype.options.icon = DefaultIcon;

const DEFAULT_MACHINE_IMAGE = '/images/mewp_machine.png';

/**
 * Pick the machine image by model / category.
 * Tries a type-specific image under /images/models/; falls back (via onError on
 * the <img>) to the default boom-lift image when that file isn't present yet.
 * Drop these files into client/public/images/models/ to enable per-type photos:
 *   boom-telescopic.png · boom-articulating.png · scissor.png · tower-light.png · battery.png
 * (You can also add an exact-model file, e.g. BT18CRT2.png, and reference it here.)
 */
const getMachineImage = (model, category) => {
  const m = String(model || '').toUpperCase();
  const cat = String(category || '').toLowerCase();
  if (cat.includes('tower')) return '/images/models/tower-light.png';
  if (cat.includes('battery')) return '/images/models/battery.png';
  if (/JCPT|SCR|SCISSOR|^SL/.test(m)) return '/images/models/scissor.png';
  if (/^BA/.test(m)) return '/images/models/boom-articulating.png';
  if (/^BT/.test(m)) return '/images/models/boom-telescopic.png';
  return DEFAULT_MACHINE_IMAGE;
};

const MEWPDataPage = () => {
    const { deviceId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const superAdmin = isSuperAdmin(user); // gates status change, SMS shortcut & editing
    const [deviceData, setDeviceData] = useState(null);
    const [trackingData, setTrackingData] = useState(null);
    const [serviceHistory, setServiceHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0); // bump to refetch (after status/edit)
    const [statusOpen, setStatusOpen] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        serial: '', model: '', brand: '', class: '', sim: '', simNo: '', sv: ''
    });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
                const [detailsResponse, trackingResponse, serviceResponse] = await Promise.allSettled([
                    mewpService.getDetails(deviceId),
                    mewpService.getDeviceTracking(deviceId),
                    mewpService.getServiceHistory(deviceId)
                ]);

                if (detailsResponse.status === 'fulfilled') {
                    setDeviceData(detailsResponse.value);
                }

                if (trackingResponse.status === 'fulfilled') {
                    setTrackingData(trackingResponse.value.success ? trackingResponse.value.data : null);
                }

                if (serviceResponse.status === 'fulfilled') {
                    setServiceHistory(serviceResponse.value.history || []);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (deviceId) {
            fetchAllData();
        }
    }, [deviceId, refreshKey]);

    // Add effect to hook up edit form
    useEffect(() => {
        if (deviceData?.device) {
            const d = deviceData.device;
            setEditForm({
                serial: d.serial_number || '',
                model: d.model_number || '',
                brand: d.brand || '',
                class: d.product_category || '',
                sim: d.mobile_number || d.sim_number || '',
                simNo: d.sim_no || '',
                sv: d.sv || '',
            });
        }
    }, [deviceData]);

    const handleEditSave = async () => {
        try {
            setSaving(true);
            setSaveError('');
            await mewpService.updateDeviceMetadata(deviceData.device?.device_id || deviceId, {
                serial_number: editForm.serial.trim(),
                model_number: editForm.model.trim(),
                brand: editForm.brand.trim(),
                product_category: editForm.class.trim(),
                mobile_number: editForm.sim.trim(),
                sim_no: editForm.simNo.trim(),
                sv: editForm.sv.trim(),
            });
            setEditOpen(false);
            // Refresh logic (to persist new data)
            const detailsResponse = await mewpService.getDetails(deviceId);
            setDeviceData(detailsResponse.data || detailsResponse);
        } catch (error) {
            console.error('Failed to update details:', error);
            setSaveError(error.response?.data?.message || error.message || 'Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    const device = useMemo(() => {
        if (!deviceData) return null;
        const d = deviceData.device || {};
        const telemetry = deviceData.latestTelemetry || {};
        const td = trackingData || {};

        return {
            id: d.device_id,
            name: d.name || 'Unnamed Asset',
            equipmentId: d.equipment_id || d.name,
            // True lifecycle status from asset_tracking (RENTAL / DE_HIRED / SERVICE /
            // BREAKDOWN / AVAILABLE / WAITING_FOR_RENT); fall back to the old binary.
            status: td.status || (td.id ? 'RENTAL' : 'AVAILABLE'),
            assetTag: d.asset_tag || '--',
            serial: d.serial_number || d.chassis_number || '--',
            class: d.product_category || 'MEWP',
            model: d.model_number || d.product_category || 'MEWP',
            imei: d.imei || d.device_id || '--',
            sim: d.mobile_number || d.sim_number || '--',
            simNo: d.sim_no || '--',
            firmware: d.sv || '--',
            liveStatus: d.live_status || d.status || 'OFF',
            liveStatusCode: resolveDeviceStatus({
                live_status: d.live_status,
                engine_speed: telemetry.engine_speed ?? telemetry['190'],
                ign: telemetry.ign ?? d.location?.ign,
                latitude: telemetry.latitude,
                longitude: telemetry.longitude,
                location: d.location,
                last_seen: d.last_data_at,
            }),
            lastSeen: d.last_data_at || null,
            coolant: (telemetry.coolant_temperature ?? telemetry['110'] ?? null),
            today: deviceData.todayActivity || {},
            shift: {
                start: deviceData.activeContract?.shift_start_time || null,
                end: deviceData.activeContract?.shift_end_time || null
            },
            batteryLevel: telemetry.fuel_level || telemetry.battery_level || 0,
            location: {
                address: td.site_location || 'Warehouse A',
                lat: parseFloat(telemetry.latitude || d.location?.lat || 20.5937),
                lon: parseFloat(telemetry.longitude || d.location?.lon || 78.9629),
            },
            engineHours: telemetry.engine_working_hours || 0,
            specs: {
                manufacturer: d.brand || 'MTANDT',
                modelYear: td.created_at ? new Date(td.created_at).getFullYear() : '2023',
                powerSource: d.device_type || 'Diesel / Electric',
            },
            rental: {
                customerName: td.contact_name || 'Not Yet',
                startDate: td.contract_start_date ? formatToIST(td.contract_start_date) : 'Not Yet',
                estReturn: td.contract_end_date ? formatToIST(td.contract_end_date) : 'Not Yet',
                remainingDays: td.contract_end_date ? Math.max(0, Math.ceil((new Date(td.contract_end_date) - new Date()) / (1000 * 60 * 60 * 24))) : 0,
                totalDays: (td.contract_start_date && td.contract_end_date) ? Math.ceil((new Date(td.contract_end_date) - new Date(td.contract_start_date)) / (1000 * 60 * 60 * 24)) : 0
            },
            tracking: td
        };
    }, [deviceData, trackingData]);

    if (loading) return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100vh" gap={2} bgcolor="background.default">
            <Skeleton variant="circular" width={60} height={60} />
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>Analyzing Asset Intelligence...</Typography>
        </Box>
    );

    if (!device) return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100vh" gap={2} bgcolor="background.default">
            <AlertCircleIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
            <Typography variant="h5" fontWeight={700}>Device Not Found</Typography>
            <Button variant="contained" onClick={() => navigate('/equipment')}>Return to Fleet</Button>
        </Box>
    );

    return (
        <Box p={4} sx={{ bgcolor: '#f8fafc', minHeight: '100vh', backgroundImage: 'radial-gradient(at 0% 0%, rgba(37, 99, 235, 0.05) 0px, transparent 50%)' }}>
            {/* Navigation Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
                <Box>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                        sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary', '&:hover': { bgcolor: 'transparent', color: 'primary.main' } }}
                    >
                        Back to Fleet
                    </Button>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <Typography variant="h3" sx={{ fontWeight: 950, color: '#0f172a', letterSpacing: '-0.05em' }}>
                            {device.name}
                        </Typography>
                        <Chip
                            label={statusLabel(device.status)}
                            color={statusColor(device.status)}
                            onClick={superAdmin ? () => setStatusOpen(true) : undefined}
                            sx={{ fontWeight: 900, borderRadius: '8px', px: 1, fontSize: '0.75rem', cursor: superAdmin ? 'pointer' : 'default' }}
                        />
                        {/* Super-admin actions: change status (add to rental etc.), SMS the SIM, edit details */}
                        {superAdmin && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button size="small" variant="outlined" startIcon={<StatusIcon />}
                                    onClick={() => setStatusOpen(true)}
                                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px' }}>
                                    Change Status
                                </Button>
                                <Button size="small" variant="outlined" startIcon={<SmsIcon />}
                                    disabled={!device.sim || device.sim === '--'}
                                    onClick={() => navigate(`/sms?sim=${encodeURIComponent(device.sim)}`)}
                                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px' }}>
                                    SMS
                                </Button>
                                <IconButton size="small" onClick={() => setEditOpen(true)} sx={{ bgcolor: alpha('#0f172a', 0.05), '&:hover': { bgcolor: alpha('#0f172a', 0.1) } }}>
                                    <EditIcon fontSize="small" color="action" />
                                </IconButton>
                            </Stack>
                        )}
                    </Box>
                    <Stack direction="row" spacing={3} mt={1.5} flexWrap="wrap" rowGap={0.5} sx={{ opacity: 0.7 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.05em' }}>
                            SN: <span style={{ color: '#0f172a' }}>{device.serial}</span>
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.05em' }}>
                            MODEL: <span style={{ color: '#0f172a' }}>{device.model}</span>
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.05em' }}>
                            DEVICE ID: <span style={{ color: '#0f172a' }}>{device.imei}</span>
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.05em' }}>
                            MOBILE: <span style={{ color: '#0f172a' }}>{device.sim}</span>
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.05em' }}>
                            TAG: <span style={{ color: '#0f172a' }}>{device.assetTag}</span>
                        </Typography>
                    </Stack>
                </Box>
                {/* Action Buttons */}
                <Stack direction="row" spacing={2}>
                    {/* Event Log removed as per request */}
                </Stack>
            </Box>

            {/* Main Content Sections */}
            <Grid container spacing={4} mt={1}>
                <Grid item xs={12} lg={4}>
                    <Stack spacing={4}>
                        {/* Equipment Visual & Identity Card */}
                        <Card sx={{ borderRadius: '32px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: 'none' }}>
                            <Box sx={{ position: 'relative', bgcolor: '#f1f5f9', p: 4, display: 'flex', justifyContent: 'center' }}>
                                <Box
                                    component="img"
                                    src={getMachineImage(device.model, device.class)}
                                    alt={device.model || 'Machine'}
                                    onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_MACHINE_IMAGE; }}
                                    sx={{ height: 260, zIndex: 1, filter: 'drop-shadow(0 25px 40px rgba(0,0,0,0.2))', objectFit: 'contain' }}
                                />
                                <MachineIcon sx={{ position: 'absolute', top: 20, left: 20, fontSize: 80, opacity: 0.03, transform: 'rotate(-15deg)' }} />
                            </Box>
                            <CardContent sx={{ p: 4 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                    <Typography variant="h6" sx={{ fontWeight: 900 }}>Telematics &amp; Identity</Typography>
                                    <Chip label={statusLabel(device.status)} color={statusColor(device.status)} size="small" sx={{ fontWeight: 800 }} />
                                </Box>

                                <Grid container spacing={2} mb={3}>
                                    <Grid item xs={6}>
                                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '16px', textAlign: 'center' }}>
                                            <Typography variant="caption" fontWeight={700} color="text.secondary">BATTERY</Typography>
                                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                <BatteryIcon sx={{ color: device.batteryLevel > 20 ? '#10b981' : '#ef4444', fontSize: 20 }} />
                                                <Typography variant="h6" fontWeight={900}>{device.batteryLevel}%</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '16px', textAlign: 'center' }}>
                                            <Typography variant="caption" fontWeight={700} color="text.secondary">ENGINE HOURS</Typography>
                                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                <ClockIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                                                <Typography variant="h6" fontWeight={900}>{device.engineHours}h</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Stack spacing={2}>
                                    {[
                                        { l: 'Equipment ID', v: device.name },
                                        { l: 'Serial Number', v: device.serial },
                                        { l: 'Device ID (IMEI)', v: device.imei },
                                        { l: 'Mobile Number', v: device.sim },
                                        { l: 'SIM No (ICCID)', v: device.simNo },
                                        { l: 'Firmware (SV)', v: device.firmware },
                                        { l: 'Model', v: device.model },
                                        { l: 'Manufacturer', v: device.specs.manufacturer },
                                    ].map((s, i) => (
                                        <Box key={i} display="flex" justifyContent="space-between" alignItems="center" sx={{ borderBottom: '1px dashed #e2e8f0', pb: 1.5 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>{s.l}</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>{s.v}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>

                        {/* Live & Today's Activity */}
                        <Card sx={{ borderRadius: '32px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6" sx={{ fontWeight: 900 }}>Live &amp; Today</Typography>
                                    <LiveStatusBadge status={device.liveStatusCode} lastSeen={device.lastSeen} />
                                </Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={2}>
                                    Last seen: {device.lastSeen ? formatToIST(device.lastSeen, 'dd MMM, hh:mm a') : 'No data'}
                                </Typography>

                                <Grid container spacing={2} mb={2}>
                                    <Grid item xs={6}>
                                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '16px', textAlign: 'center' }}>
                                            <Typography variant="caption" fontWeight={700} color="text.secondary">ENGINE HOURS</Typography>
                                            <Typography variant="h6" fontWeight={900}>{device.engineHours}h</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '16px', textAlign: 'center' }}>
                                            <Typography variant="caption" fontWeight={700} color="text.secondary">COOLANT</Typography>
                                            <Typography variant="h6" fontWeight={900}>{device.coolant != null ? `${device.coolant}°C` : '--'}</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ letterSpacing: '0.08em' }}>TODAY'S ACTIVITY</Typography>
                                <Stack spacing={1.2} mt={1}>
                                    {[
                                        { l: 'Ignition', on: device.today.ign_today, ts: device.today.last_ign_on },
                                        { l: 'Engine', on: device.today.engine_today, ts: device.today.last_engine_on },
                                        { l: 'CAN data', on: device.today.can_today, ts: device.today.last_can },
                                    ].map((r, i) => (
                                        <Box key={i} display="flex" justifyContent="space-between" alignItems="center">
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.on ? '#10b981' : '#ef4444' }} />
                                                <Typography variant="body2" fontWeight={700}>{r.l}</Typography>
                                            </Box>
                                            <Typography variant="caption" fontWeight={700} sx={{ color: r.on ? '#15803d' : '#ef4444' }}>
                                                {r.on ? `today ${formatToIST(r.ts, 'hh:mm a')}` : (r.ts ? `last ${formatToIST(r.ts, 'dd MMM, hh:mm a')}` : 'Not yet')}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>

                <Grid item xs={12} lg={8}>
                    <Stack spacing={4}>
                        {/* Status Context Card */}
                        <Card sx={{ borderRadius: '24px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 4 }}>
                                {(device.status === 'RENTAL' || device.status === 'RENTED') ? (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 950, color: 'text.secondary', mb: 3, letterSpacing: '0.1em' }}>RENTAL DETAILS</Typography>
                                        <Grid container spacing={4}>
                                            <Grid item xs={12} md={7}>
                                                <Box display="flex" alignItems="center" gap={3} mb={4}>
                                                    <Avatar sx={{ width: 72, height: 72, bgcolor: '#eff6ff', color: '#2563eb', fontWeight: 800, fontSize: '1.75rem', borderRadius: '20px' }}>
                                                        {device.rental.customerName.charAt(0)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.05em' }}>CLIENT</Typography>
                                                        <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>{device.rental.customerName}</Typography>
                                                        <Chip
                                                            icon={<MapPinIcon sx={{ fontSize: '14px !important' }} />}
                                                            label={device.location.address}
                                                            size="small"
                                                            sx={{ mt: 1, bgcolor: 'transparent', border: '1px solid #e2e8f0', fontWeight: 600, color: 'text.secondary' }}
                                                        />
                                                    </Box>
                                                </Box>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <Box sx={{ p: 2, border: '1px solid #f1f5f9', borderRadius: '16px' }}>
                                                            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>CONTRACT START</Typography>
                                                            <Stack direction="row" alignItems="center" gap={1}>
                                                                <CalendarIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                                                                <Typography variant="subtitle1" fontWeight={800}>{device.rental.startDate}</Typography>
                                                            </Stack>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Box sx={{ p: 2, border: '1px solid #f1f5f9', borderRadius: '16px' }}>
                                                            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>EST. RETURN</Typography>
                                                            <Stack direction="row" alignItems="center" gap={1}>
                                                                <HistoryIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                                                                <Typography variant="subtitle1" fontWeight={800}>{device.rental.estReturn}</Typography>
                                                            </Stack>
                                                        </Box>
                                                    </Grid>
                                                    {device.shift.start && (
                                                        <Grid item xs={12}>
                                                            <Box sx={{ p: 2, border: '1px solid #f1f5f9', borderRadius: '16px' }}>
                                                                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>DAILY SHIFT WINDOW</Typography>
                                                                <Stack direction="row" alignItems="center" gap={1}>
                                                                    <ClockIcon sx={{ fontSize: 18, color: '#10b981' }} />
                                                                    <Typography variant="subtitle1" fontWeight={800}>
                                                                        {String(device.shift.start).slice(0, 5)}{device.shift.end ? ` – ${String(device.shift.end).slice(0, 5)}` : ''}
                                                                    </Typography>
                                                                </Stack>
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12} md={5}>
                                                <Box sx={{ height: 200, borderRadius: '24px', overflow: 'hidden', border: '4px solid white', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                                                    <MapContainer center={[device.location.lat, device.location.lon]} zoom={13} style={{ height: '100%', width: '100%' }}>
                                                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                                        <Marker position={[device.location.lat, device.location.lon]} />
                                                    </MapContainer>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </>
                                ) : device.status === 'SERVICE' ? (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 950, color: '#f59e0b', mb: 3, letterSpacing: '0.1em' }} display="flex" alignItems="center" gap={1}>
                                            <WrenchIcon fontSize="small" /> UNDER MAINTENANCE
                                        </Typography>
                                        <Box display="flex" gap={4} alignItems="center">
                                            <Box flexGrow={1}>
                                                <Typography variant="h5" fontWeight={900} gutterBottom>Annual Hydraulic Service</Typography>
                                                <Typography variant="body1" color="text.secondary" fontWeight={600} mb={3}>Serviced by: <span style={{ color: '#0f172a' }}>Global Hydraulics Pvt Ltd</span></Typography>

                                                <Grid container spacing={3}>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" fontWeight={700} color="text.secondary">SERVICE STARTED</Typography>
                                                        <Typography variant="subtitle1" fontWeight={800}>Oct 12, 2024</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" fontWeight={700} color="text.secondary">EXP. COMPLETION</Typography>
                                                        <Typography variant="subtitle1" fontWeight={800}>Oct 15, 2024</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Box>
                                    </>
                                ) : (
                                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="200px" sx={{ opacity: 0.7 }}>
                                        <MachineIcon sx={{ fontSize: 60, mb: 2, color: '#10b981' }} />
                                        <Typography variant="h5" fontWeight={800} color="#0f172a">Ready for Deployment</Typography>
                                        <Typography variant="body2" fontWeight={600} color="text.secondary">Asset is available in {device.location.address}</Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                        {/* Service History Section */}
                        <Card sx={{ borderRadius: '24px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardHeader
                                title="Service History"
                                titleTypographyProps={{ variant: 'h6', fontWeight: 900, letterSpacing: '0.05em' }}
                                action={
                                    <Button variant="text" size="small" sx={{ fontWeight: 700 }}>View All</Button>
                                }
                                sx={{ p: 3, pb: 0 }}
                            />
                            <CardContent sx={{ p: 0 }}>
                                <TableContainer>
                                    <Table sx={{ minWidth: 650 }} aria-label="service history table">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary', borderBottom: '1px solid #e2e8f0', py: 2 }}>DATE</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary', borderBottom: '1px solid #e2e8f0', py: 2 }}>SERVICE TYPE</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary', borderBottom: '1px solid #e2e8f0', py: 2 }}>PROVIDER</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary', borderBottom: '1px solid #e2e8f0', py: 2 }} align="right">STATUS</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {serviceHistory.length > 0 ? (
                                                serviceHistory.map((row, index) => (
                                                    <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                        <TableCell component="th" scope="row" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                                            {row.completed_at ? formatToIST(row.completed_at) : (row.scheduled_date ? formatToIST(row.scheduled_date) : 'Not Yet')}
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{row.maintenance_type || row.description}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{row.provider || 'Internal'}</TableCell>
                                                        <TableCell align="right">
                                                            <Chip
                                                                label={row.status ? row.status.toUpperCase() : 'UNKNOWN'}
                                                                size="small"
                                                                sx={{
                                                                    fontWeight: 900,
                                                                    borderRadius: '6px',
                                                                    fontSize: '0.7rem',
                                                                    bgcolor: (row.status === 'In Progress' || row.status === 'OPEN') ? alpha('#f59e0b', 0.1) : alpha('#10b981', 0.1),
                                                                    color: (row.status === 'In Progress' || row.status === 'OPEN') ? '#f59e0b' : '#10b981'
                                                                }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                                                        <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                            No service history available yet (Not Yet)
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>
            </Grid>

            {/* Edit Machine & Device Dialog — super admin only */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
                    Edit Machine &amp; Device
                    <Typography variant="body2" color="text.secondary">{machineLabel({ serial_number: device.serial, equipment_id: device.equipmentId })} • TCU {device.imei}</Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2.5} pt={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Serial Number (SN)" value={editForm.serial}
                                onChange={(e) => setEditForm(prev => ({ ...prev, serial: e.target.value }))} fullWidth size="small" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Model" value={editForm.model}
                                onChange={(e) => setEditForm(prev => ({ ...prev, model: e.target.value }))} fullWidth size="small" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Manufacturer / Brand" value={editForm.brand}
                                onChange={(e) => setEditForm(prev => ({ ...prev, brand: e.target.value }))} fullWidth size="small" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Category (e.g. Boom Lift)" value={editForm.class}
                                onChange={(e) => setEditForm(prev => ({ ...prev, class: e.target.value }))} fullWidth size="small" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Mobile Number (SIM's MSISDN)" value={editForm.sim}
                                onChange={(e) => setEditForm(prev => ({ ...prev, sim: e.target.value }))} fullWidth size="small" />
                            <TextField label="SIM No (ICCID, on the pouch)" value={editForm.simNo}
                                onChange={(e) => setEditForm(prev => ({ ...prev, simNo: e.target.value }))} fullWidth size="small" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Firmware (SV)" value={editForm.sv}
                                onChange={(e) => setEditForm(prev => ({ ...prev, sv: e.target.value }))} fullWidth size="small" />
                        </Grid>
                    </Grid>
                    {saveError && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1.5, fontWeight: 700 }}>{saveError}</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEditOpen(false)} color="inherit" sx={{ fontWeight: 700 }}>Cancel</Button>
                    <Button onClick={handleEditSave} disabled={saving} variant="contained" disableElevation sx={{ fontWeight: 700, borderRadius: '8px' }}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Change Machine Status (add to rental / de-hire / service ...) — super admin only */}
            <ChangeStatusDialog
                open={statusOpen}
                onClose={() => setStatusOpen(false)}
                device={{ equipment_id: device.equipmentId, device_id: device.id, model_number: device.model, serial_number: device.serial }}
                currentStatus={device.status}
                onSuccess={() => setRefreshKey(k => k + 1)}
            />
        </Box>
    );
};

export default MEWPDataPage;
